import { get, post, put, del } from '../../api/api.service';
import i18n from '../../../translation/i18n';

export interface CertificateDataParams {
    fromDate: string;
    toDate: string;
}

export interface CertificateMonthData {
    monthYear: string;
    totalAmount: number;
    totalHours: number;
    totalPerUnitAmount: number;
}

export interface CertificateDataResponse {
    statusCode: number;
    message: string;
    response: CertificateMonthData[];
    totalCount: number;
    numberOfPages: number;
}

export const getCertificateDataApi = (params: CertificateDataParams): Promise<CertificateDataResponse> => {
    return get(`/employercertificatev2/get-certificate-data?fromDate=${params.fromDate}&toDate=${params.toDate}`);
};

// ─── Submit Certificate ───────────────────────────────────────────────────────

export interface EmployeeWagesDetail {
    month: string;
    hoursworked: number;
    absence: number;
    overtime: number;
    totalAmount: number;
    breakDuration?: number;
    totalWorkingDays: number;
    hoursDate: string;
}

export interface CertificateDetailPerMonth {
    monthdate: string;
    numberOfDaysWorked: number;
    dutyHours: number;
    nOKMonth: number;
    otherRemunerationEnterArt: number;
    nOKMonthAmount: number;
}

export interface SubmitCertificatePayload {
    draftCertificateId?: number;
    selectPeriod: boolean;
    individualInvoices: boolean;
    firstName: string;
    lastName: string;
    socialSecurityNumber: string;
    employmentPeriodFrom: string;
    employmentPeriodEnd: string;
    isStillEmployed: boolean;
    employeePosition: string;
    reportedTimeFromDate: string;
    reportedTimeEndDate: string;
    teachingHours: number;
    salaryRefers: string;
    amountForOverTime: number;
    userName: string;
    employerName: string;
    streetAddress: string;
    mailingAddress: string;
    organizationNumber: string;
    telephoneNumber: string;
    otherInfomationEmployment: string;
    otherReason: string;
    isFullTimehoursPerWeek: boolean;
    isPartTimehoursPerWeek: boolean;
    isVaryingHours: boolean;
    employeeOwnRequest: boolean;
    isOtherReason: boolean;
    isContinueWorkFrom: boolean;
    isUntilFurtherNotice: boolean;
    isContinueVariableWorkingHours: boolean;
    isContinueEmployeeAccepted: boolean;
    isTimeWorkedUntilFurtherNotice: boolean;
    isTeachingHours: boolean;
    isMonthlySalary: boolean;
    isWeeklySalary: boolean;
    isDalilySalary: boolean;
    isHourlySalary: boolean;
    isFillingWorkvaried: boolean;
    isSalaryInAdditional: boolean;
    isEmployeeWithHoliday: boolean;
    isEmployeeWithLivingWage: boolean;
    isCrossInstedofTick: boolean;
    isTickInCorrectly: boolean;
    specialInfoEmployment: boolean;
    isCompensationTermination: boolean;
    EmployeeWagesDetails: EmployeeWagesDetail[];
    CertificateDetailsPerMonth: CertificateDetailPerMonth[];
}

export interface SubmitCertificateResponse {
    statusCode: number;
    message: string;
    response: any;
}

export const submitCertificateApi = (payload: SubmitCertificatePayload): Promise<SubmitCertificateResponse> => {
    const lang = i18n.language || 'sv-se';
    return post<SubmitCertificateResponse>(`/employercertificatev2?lang=${lang}`, payload);
};

// ─── Save Draft Certificate ──────────────────────────────────────────────────

export interface SaveDraftPayload extends SubmitCertificatePayload {
    isDraft?: boolean;
}

export const saveDraftCertificateApi = (payload: SaveDraftPayload): Promise<SubmitCertificateResponse> => {
    const lang = i18n.language || 'sv-se';
    return post<SubmitCertificateResponse>(`/draftemployeecertificatev2?lang=${lang}`, payload);
};

// ─── Get Employee Certificates List ──────────────────────────────────────────

export interface EmployeeCertificate {
    certificateId: number;
    firstName: string;
    lastName: string;
    socialSecurityNumber: string;
    employmentPeriodFrom: string | null;
    employmentPeriodEnd: string | null;
    isStillEmployed: boolean;
    createdDate: string;
    employeeWagesDetails: {
        id: number;
        certificateId: number;
        month: string;
        hoursworked: number;
        overtime: number;
        absence: number;
        hoursDate: string;
    }[];
}

export interface EmployeeCertificatesResponse {
    statusCode: number;
    message: string;
    response: EmployeeCertificate[];
    totalCount: number;
    numberOfPages: number;
}

export interface GetCertificatesParams {
    fromDate?: string;
    toDate?: string;
    searchQuery?: string;
}

export const getEmployeeCertificatesApi = (params?: GetCertificatesParams): Promise<EmployeeCertificatesResponse> => {
    const query = new URLSearchParams();
    if (params?.fromDate) query.append('fromDate', params.fromDate);
    if (params?.toDate) query.append('toDate', params.toDate);
    if (params?.searchQuery) query.append('searchQuery', params.searchQuery);
    const qs = query.toString();
    return get(`/employercertificatev2${qs ? `?${qs}` : ''}`);
};

// ─── Get Draft Employee Certificates List ────────────────────────────────────

export const getDraftEmployeeCertificatesApi = (params?: GetCertificatesParams): Promise<EmployeeCertificatesResponse> => {
    const query = new URLSearchParams();
    if (params?.fromDate) query.append('fromDate', params.fromDate);
    if (params?.toDate) query.append('toDate', params.toDate);
    if (params?.searchQuery) query.append('searchQuery', params.searchQuery);
    const qs = query.toString();
    return get(`/draftemployeecertificatev2${qs ? `?${qs}` : ''}`);
};

// ─── Delete Certificate ───────────────────────────────────────────────────────

export const deleteCertificateApi = (certificateId: number): Promise<any> => {
    return put<any>(`/employercertificatev2/${certificateId}`, {});
};

export const deleteDraftCertificateApi = (certificateId: number): Promise<any> => {
    return del<any>(`/draftemployeecertificatev2/${certificateId}`);
};

// ─── Get Certificate By Id ────────────────────────────────────────────────────

export interface CertificateByIdResponse {
    statusCode: number;
    message: string;
    response: EmployeeCertificate;
}

export const getCertificateByIdApi = (certificateId: number): Promise<CertificateByIdResponse> => {
    return get(`/employercertificatev2/${certificateId}`);
};

export const getDraftCertificateByIdApi = (certificateId: number): Promise<CertificateByIdResponse> => {
    return get(`/draftemployeecertificatev2/${certificateId}`);
};

// ─── Download Certificate ─────────────────────────────────────────────────────

// ─── Update Certificate ──────────────────────────────────────────────────────

export const updateCertificateApi = (payload: any): Promise<any> => {
    const lang = i18n.language || 'sv-se';
    return put<any>(`/employercertificatev2/update-certificate-api?lang=${lang}`, payload);
};

export const updateDraftCertificateApi = (certificateId: number, payload: any): Promise<any> => {
    return put<any>(`/draftemployeecertificatev2/${certificateId}`, payload);
};

// ─── Download Certificate ─────────────────────────────────────────────────────

// ─── Get Employee By ID ───────────────────────────────────────────────────────

export interface EmployeeData {
    userId: number;
    firstName: string;
    lastName: string;
    ssn: string;
    phoneNo: string;
}

export interface EmployeeByIdResponse {
    statusCode: number;
    message: string;
    response: EmployeeData;
}

export const getEmployeeByIdApi = (userId: number): Promise<EmployeeByIdResponse> => {
    return get(`/employee/get/${userId}`);
};

export const downloadCertificateApi = async (certificateId: number): Promise<void> => {
    const userData = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
    })();
    const token = userData.token || localStorage.getItem('token') || localStorage.getItem('authToken') || '';
    const baseUrl = (import.meta as any).env.VITE_API_URL || '';
    const url = `${baseUrl}/employercertificatev2/${certificateId}/download-certificate?locale=sv-se`;

    /* Plain GET with the id in the path and no request body — the shape
       verified against the running API:
         GET {base}/employercertificatev2/{id}/download-certificate?locale=sv-se
         accept: *|*   ·   Authorization: Bearer <token>

       Stays on raw `fetch` rather than the shared apiClient because the
       response is a PDF blob: axios would hand error bodies back as blobs
       too, which defeats the interceptor's `error.response.data.message`
       lookup and would fire a second, unreadable global alert on top of
       the caller's own. */
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            accept: '*/*',
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) throw new Error(`Download failed: ${response.status}`);

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `certificate-${certificateId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
};
